/**
 * Map Upsell Banner Component
 * 
 * Shows a subtle floating banner at the bottom of the map for BASIC tier providers
 * (those with no subscription or hidden from map).
 * 
 * Rules:
 * - Only shown to logged-in providers
 * - Only shown if provider is BASIC tier with NO active subscription
 * - Frequency cap: max 1x per day (localStorage based)
 * - Dismissible for 24 hours
 */

import React, { useState, useEffect } from 'react';
import { X, Zap, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/context';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'keikka_map_upsell_dismiss';
const IMPRESSION_KEY = 'keikka_map_upsell_impression';

export function MapUpsellBanner({ onBoostClick }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [providerData, setProviderData] = useState(null);

  useEffect(() => {
    checkEligibility();
  }, []);

  const checkEligibility = async () => {
    // Check if provider is logged in
    const sessionToken = localStorage.getItem('provider_session');
    if (!sessionToken) {
      return; // Not a provider - don't show
    }

    // Check dismiss timestamp (24h frequency cap)
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      const now = Date.now();
      const hoursSinceDismiss = (now - dismissTime) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 24) {
        return; // Still within dismiss period
      }
    }

    // Check daily impression cap
    const lastImpression = localStorage.getItem(IMPRESSION_KEY);
    if (lastImpression) {
      const impressionDate = new Date(parseInt(lastImpression, 10)).toDateString();
      const today = new Date().toDateString();
      if (impressionDate === today) {
        return; // Already shown today
      }
    }

    try {
      // Fetch provider's map settings
      const response = await fetch(`${BACKEND_URL}/api/map/my/settings`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      const settings = data.settings || {};
      const tier = settings.map_tier || 'basic';
      const subscriptionStatus = settings.subscription_status || 'inactive';

      // ONLY show for BASIC tier providers who are:
      // - subscription_status = 'inactive' (no subscription)
      // - OR hidden from map (map_visible = false)
      // DO NOT show if already boosted/premium or has active subscription
      
      if (tier === 'boost' || tier === 'premium') {
        return; // Already boosted - don't show
      }
      
      if (subscriptionStatus === 'active') {
        return; // Already has active subscription - don't show
      }

      // Provider is BASIC with no active subscription - show the upsell
      setProviderData({ settings, tier, subscriptionStatus });
      setVisible(true);

      // Record impression
      localStorage.setItem(IMPRESSION_KEY, Date.now().toString());

      // Track analytics - boost_prompt_viewed
      trackEvent('boost_prompt_viewed');

    } catch (error) {
      console.error('Error checking upsell eligibility:', error);
    }
  };

  const trackEvent = async (eventType) => {
    try {
      await axios.post(`${BACKEND_URL}/api/map/analytics`, null, {
        params: { event_type: eventType }
      });
    } catch (e) {
      // Silent fail for analytics
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    trackEvent('boost_dismissed');
  };

  const handleBoostClick = async () => {
    // Track analytics - boost_clicked
    trackEvent('boost_clicked');
    
    if (onBoostClick) {
      onBoostClick();
    } else {
      // Default: navigate to dashboard with upgrade param
      window.location.href = '/provider/dashboard?upgrade=boost';
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div 
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 w-[90%] max-w-md animate-in slide-in-from-bottom-4 duration-300"
      data-testid="map-upsell-banner"
    >
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-lg p-4 relative">
        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded transition-colors"
          aria-label={t('upsell.notNow') || 'Not now'}
          data-testid="upsell-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="bg-white/20 rounded-full p-2 flex-shrink-0">
            <Zap className="w-5 h-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm mb-1">
              {t('upsell.boostTitle') || 'Boost your visibility'}
            </h4>
            <p className="text-xs text-white/90 mb-3">
              {t('upsell.boostBody') || 'Get more clients by appearing higher on the map and standing out.'}
            </p>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white text-green-700 hover:bg-white/90 text-xs h-8"
                onClick={handleBoostClick}
                data-testid="upsell-boost-btn"
              >
                <TrendingUp className="w-3 h-3 mr-1" />
                {t('upsell.boostNow') || 'Boost now'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="text-white/80 hover:text-white hover:bg-white/10 text-xs h-8"
                onClick={handleDismiss}
                data-testid="upsell-not-now-btn"
              >
                {t('upsell.notNow') || 'Not now'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MapUpsellBanner;
